import { useSuspenseQuery } from '@tanstack/react-query';
import { IdentityTaskDto, IdentityTaskStatus, isTaskFor } from '../../api/app.types';
import { AppBaseDto } from '../../api/graph.types';
import { AppOverviewDto, Credential, ExpirationType, isSamlApp } from '../model';
import { appQueries } from '../../api/app.api';
import { useFilterContext } from '../state';
import { useMemo } from 'react';
import useExpiryClassification from './useExpiryClassification';
import { useFilteredTasks } from '../state/filter-tasks';

export default function useGroupByExpiration(apps: AppBaseDto[], expirationType?: ExpirationType) {
  const { data: allTasks } = useSuspenseQuery(appQueries.getTasks);
  const tasks = useFilteredTasks(allTasks);
  const { hideClosedTasks } = useFilterContext();
  const { isAboutToExpire, isExpired, isManaged } = useExpiryClassification();

  const expiringApps = useMemo(() => {
    if (expirationType !== ExpirationType.AboutToExpire && expirationType !== undefined) return []; // Don't compute them if they are not required

    return apps
      .map((app) => toAppOverview(app, tasks, hideClosedTasks, ExpirationType.AboutToExpire))
      .filter((app) => !isManaged(app.id, app.owners) && isAboutToExpire(app.credentialDate, isSamlApp(app)));
  }, [apps, tasks, hideClosedTasks, expirationType, isManaged, isAboutToExpire]);

  const expiredApps = useMemo(() => {
    if (expirationType !== ExpirationType.Expired && expirationType !== undefined) return []; // Don't compute them if they are not required

    return apps
      .map((app) => toAppOverview(app, tasks, hideClosedTasks, ExpirationType.Expired))
      .filter((app) => !isManaged(app.id, app.owners) && isExpired(app.credentialDate));
  }, [apps, tasks, hideClosedTasks, expirationType, isManaged, isExpired]);

  const managedApps = useMemo(() => {
    if (expirationType !== ExpirationType.Managed && expirationType !== undefined) return []; // Don't compute them if they are not required

    return apps
      .map((app) => toAppOverview(app, tasks, hideClosedTasks, ExpirationType.Managed))
      .filter((app) => isManaged(app.id, app.owners));
  }, [apps, tasks, hideClosedTasks, expirationType, isManaged]);

  const allApps = useMemo(() => {
    return apps.map((app) => toAppOverview(app, tasks, hideClosedTasks));
  }, [apps, tasks, hideClosedTasks]);

  return {
    expiringApps,
    expiredApps,
    managedApps,
    allApps,
  };
}

function toAppOverview(
  app: AppBaseDto,
  tasks: IdentityTaskDto[],
  hideClosedTasks: boolean,
  expirationType?: ExpirationType
): AppOverviewDto {
  let keyCreds = app.keyCredentials;
  if (hideClosedTasks) {
    keyCreds = keyCreds.filter(
      (p) =>
        !tasks.some((t) => isTaskFor(t, p.keyId, app.id) && t.status?.toString() === IdentityTaskStatus.Done.toString())
    );
  }

  let passwordCreds = app.passwordCredentials;
  if (hideClosedTasks) {
    passwordCreds = passwordCreds.filter(
      (p) =>
        !tasks.some((t) => isTaskFor(t, p.keyId, app.id) && t.status?.toString() === IdentityTaskStatus.Done.toString())
    );
  }

  const pastCredential = getNearestCredential(passwordCreds, keyCreds, false);
  const upcomingCredential = getNearestCredential(passwordCreds, keyCreds, true);

  const pastDateTask = tasks.find((p) => isTaskFor(p, pastCredential?.keyId, app.id));
  const upcomingDateTask = tasks.find((p) => isTaskFor(p, upcomingCredential?.keyId, app.id));

  const pastDate = pastCredential?.endDateTime ? new Date(pastCredential?.endDateTime) : undefined;
  const upcomingDate = upcomingCredential?.endDateTime ? new Date(upcomingCredential?.endDateTime) : undefined;

  let task: IdentityTaskDto | undefined = undefined;
  switch (expirationType) {
    case ExpirationType.AboutToExpire:
      task = upcomingDateTask;
      break;

    case ExpirationType.Expired:
      task = pastDateTask;
      break;

    case ExpirationType.Managed:
      break;

    default:
      task = pastDateTask ?? upcomingDateTask;
      break;
  }

  return {
    ...app,
    passwordCredentials: passwordCreds,
    keyCredentials: keyCreds,
    credentialDate:
      expirationType === ExpirationType.AboutToExpire
        ? upcomingDate
        : expirationType === ExpirationType.Expired
          ? pastDate
          : (pastDate ?? upcomingDate),
    taskIsAssigned: task?.ownerId !== undefined,
    taskStatus: task?.status,
  };
}

function getNearestCredential(
  passwordCredentials: Credential[],
  keyCredentials: Credential[],
  future: boolean
): Credential | undefined {
  const nearestPasswordCredential = getClosestCredential(passwordCredentials, future);
  const nearestKeyCredential = getClosestCredential(keyCredentials, future);

  if (nearestPasswordCredential && nearestKeyCredential) {
    return new Date(nearestPasswordCredential.endDateTime!).getTime() <
      new Date(nearestKeyCredential.endDateTime!).getTime()
      ? nearestPasswordCredential
      : nearestKeyCredential;
  }

  return nearestPasswordCredential || nearestKeyCredential;
}

function getClosestCredential(credentials: Credential[], future: boolean): Credential | undefined {
  if (!credentials.length) return undefined;

  const currentDate = new Date();

  return credentials
    .filter((c) => c.endDateTime && !isNaN(new Date(c.endDateTime).getTime())) // Remove invalid dates
    .filter((c) => {
      const expirationDate = new Date(c.endDateTime!);
      return future ? expirationDate > currentDate : expirationDate <= currentDate;
    })
    .reduce(
      (closest, c) => {
        if (!closest) return c;
        const closestDate = new Date(closest.endDateTime!);
        const currentDate = new Date(c.endDateTime!);
        return future
          ? currentDate.getTime() < closestDate.getTime()
            ? c
            : closest
          : currentDate.getTime() > closestDate.getTime()
            ? c
            : closest;
      },
      undefined as Credential | undefined
    );
}
